import { useReducer } from 'react'

type ObjectNotFunction = { [k: string]: unknown } & { call?: never }

type StorableValue = undefined | null | string | number | boolean | ObjectNotFunction

function useSessionStorage<T extends StorableValue>(
    key: string,
    initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
    return useReducer(
        (currentState: T, action: T | ((prev: T) => T)): T => {
            try {
                const newValue = typeof action === 'function' ? (action as (prev: T) => T)(currentState) : action

                if (newValue === undefined || newValue === null) {
                    window.sessionStorage.removeItem(key)
                } else {
                    window.sessionStorage.setItem(key, JSON.stringify(newValue))
                }

                return newValue
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`[useSessionStorage] Error setting "${key}":`, error)
                return currentState
            }
        },
        initialValue,
        (fallback): T => {
            if (typeof window === 'undefined') return fallback

            const raw = window.sessionStorage.getItem(key)
            if (raw === null) return fallback

            try {
                return JSON.parse(raw) as T
            } catch {
                // Fallback: token legacy sin serializar - migrar al formato correcto
                // Validate that the fallback type is compatible with the raw string
                if (typeof fallback === 'string') {
                    try {
                        window.sessionStorage.setItem(key, JSON.stringify(raw))
                    } catch {
                        // ignore storage errors
                    }
                    return raw as unknown as T
                }

                // Type mismatch — log and return the initial fallback value
                // eslint-disable-next-line no-console
                console.warn(
                    `[useSessionStorage] Type mismatch when migrating legacy value for "${key}": expected "${typeof fallback}", got "string". Using fallback value instead.`
                )
                return fallback
            }
        }
    )
}

export default useSessionStorage
